import React, { useState, useEffect, useRef } from 'react';
import { TransactionApi, PaymentApi, ReceiptApi, CurrencyObject, Languages } from 'common/types';
import { Typography } from '@mui/material';
import ReactToPrint from 'react-to-print';
import RefundDialog from 'components/RefundDialog';
import { AmountObject } from 'common/types/adyen';
import errorHandler from 'common/services/errorHandler';
import PaymentCard from '../components/PaymentCard';
import PaymentReceipt from 'components/PaymentReceipt';

import {
	sortDepositFirst,
	capturePayment,
	CapturePaymentProps,
	getPaymentsWithModifications,
} from './utils';
import { cancelPayment, refundPayment } from './utils';
import CaptureTransactionDialog from '../components/CaptureTransactionDialog';
import CancelTransactionDialog from '../components/CancelTransactionDialog';
import { api } from 'common/frontend/api';
import { groupBy } from 'lodash';

let removePaymentsListener: () => void = () => undefined;
let removeTransactionsListener: () => void = () => undefined;

interface OwnProps {
	orderId: string;
	shopId: string;
	currency: CurrencyObject;
	userEmail: string;
	userLanguage: Languages | undefined;
}

type Props = OwnProps;

export type PaymentModifyAction = 'REFUND' | 'CANCEL' | 'CAPTURE';

const PaymentsList = (props: Props) => {
	const { orderId, shopId } = props;
	const [payments, setPayments] = useState<PaymentApi[]>([]);
	const [transactions, setTransactions] = useState<TransactionApi[]>([]);
	const [paymentError, setPaymentError] = useState<string>('');
	const [transactionsLoading, setTransactionsLoading] = useState<string[]>([]);
	const [receiptToPrint, setReceiptToPrint] = useState<ReceiptApi | null>(null);
	const receiptRef = useRef<HTMLDivElement | null>(null);
	const [transactionModifyAction, setTransactionModifyAction] = useState<{
		transaction: TransactionApi;
		action: PaymentModifyAction;
	} | null>(null);
	const transactionsByPaymentId = groupBy(transactions, 'paymentId');
	const paymentsWithModifications = getPaymentsWithModifications({
		payments,
		transactions: transactionsByPaymentId,
	});

	useEffect(() => {
		removePaymentsListener();
		removeTransactionsListener();
		removePaymentsListener = api()
			.payments.get.where('orderId', '==', orderId)
			.where('shopId', '==', shopId)
			.orderBy('timestamp', 'asc')
			.onSnapshot(
				(payments) => {
					const sortedPayments = payments.sort(sortDepositFirst);
					setPayments(sortedPayments);
				},
				(error) => {
					errorHandler.report(error);
				},
			);
		removeTransactionsListener = api()
			.transactions.get.where('orderId', '==', orderId)
			.where('shopId', '==', shopId)
			.onSnapshot(
				(transactions) => {
					setTransactions(transactions);
				},
				(error) => {
					errorHandler.report(error);
				},
			);
		return () => {
			removePaymentsListener();
			removeTransactionsListener();
		};
	}, [orderId, shopId]);

	useEffect(() => {
		if (receiptToPrint) {
			const printButton = document.getElementById('print-paylist-receipt-button');
			if (printButton) {
				printButton.click();
			}
		}
	}, [receiptToPrint]);

	const startReceiptPrint = async (receiptId: string) => {
		const receipt = await api().receipts.doc(receiptId).get();
		if (receipt) {
			setReceiptToPrint(receipt);
		}
	};

	const handleTransactionRefund = async (refundAmount: AmountObject) => {
		if (!transactionModifyAction || transactionModifyAction.action !== 'REFUND') {
			return;
		}
		const transaction = transactionModifyAction.transaction;
		setTransactionsLoading([...transactionsLoading, transaction.id]);
		try {
			await refundPayment(transaction, refundAmount);
			setTransactionModifyAction(null);
		} catch (e) {
			setPaymentError(e.message);
		} finally {
			setTransactionsLoading(transactionsLoading.filter((id) => id !== transaction.id));
		}
	};

	const handleTransactionCancel = async () => {
		if (!transactionModifyAction || transactionModifyAction.action !== 'CANCEL') {
			return;
		}
		const transaction = transactionModifyAction.transaction;
		setTransactionsLoading([...transactionsLoading, transaction.id]);
		try {
			await cancelPayment(transaction);
			setTransactionModifyAction(null);
		} catch (e) {
			setPaymentError(e.message);
		} finally {
			setTransactionsLoading(transactionsLoading.filter((id) => id !== transaction.id));
		}
	};

	const handleTransactionCapture = async (data: CapturePaymentProps) => {
		if (!transactionModifyAction || transactionModifyAction.action !== 'CAPTURE') {
			return;
		}
		const transaction = transactionModifyAction.transaction;
		setTransactionsLoading([...transactionsLoading, transaction.id]);
		try {
			await capturePayment(transaction, data);
			setTransactionModifyAction(null);
		} catch (e) {
			setPaymentError(e.message);
		} finally {
			setTransactionsLoading(transactionsLoading.filter((id) => id !== transaction.id));
		}
	};

	const renderModifyActionDialog = (
		modifyObject: { transaction: TransactionApi; action: PaymentModifyAction } | null,
	) => {
		if (!modifyObject) {
			return null;
		}
		const { action, transaction } = modifyObject;
		const transactionLoading = transactionsLoading.includes(transaction.id);
		switch (action) {
			case 'REFUND':
				return (
					<RefundDialog
						open={true}
						paymentObject={transaction.amount}
						onClose={() => setTransactionModifyAction(null)}
						refundLoading={transactionLoading}
						refundError={paymentError}
						handleRefund={handleTransactionRefund}
						shopCurrency={props.currency}
					/>
				);
			case 'CANCEL':
				return (
					<CancelTransactionDialog
						loading={transactionLoading}
						onRelease={handleTransactionCancel}
						onCancel={() => setTransactionModifyAction(null)}
						provider={transaction.provider}
					/>
				);
			case 'CAPTURE':
				return (
					<CaptureTransactionDialog
						userEmail={props.userEmail}
						userLanguage={props.userLanguage}
						paymentObject={transaction.amount}
						shopCurrency={props.currency}
						onCancel={() => setTransactionModifyAction(null)}
						onCharge={handleTransactionCapture}
						loading={transactionLoading}
						error={paymentError}
						provider={transaction.provider}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div>
			{paymentsWithModifications.map((paymentWithModifications) => {
				return (
					<PaymentCard
						key={paymentWithModifications.original.payment.id}
						paymentWithModifications={paymentWithModifications}
						handleReceiptPrint={startReceiptPrint}
						transactionsLoading={transactionsLoading}
						modifyTransaction={(t, modifyAction) =>
							setTransactionModifyAction({ transaction: t, action: modifyAction })
						}
						shopCurrency={props.currency}
					/>
				);
			})}
			{paymentError && (
				<Typography variant="caption" color="error">
					{paymentError}
				</Typography>
			)}
			{renderModifyActionDialog(transactionModifyAction)}
			{receiptToPrint && (
				<>
					<ReactToPrint
						trigger={() => (
							<button id="print-paylist-receipt-button" style={{ display: 'none' }}>
								{'Print'}
							</button>
						)}
						content={() => receiptRef.current!}
						onAfterPrint={() => setReceiptToPrint(null)}
					/>
					<div style={{ display: 'none' }}>
						<PaymentReceipt receipt={receiptToPrint} ref={receiptRef as any} />
					</div>
				</>
			)}
		</div>
	);
};

export default PaymentsList;
